/** @jsx jsx */
import { jsx } from "theme-ui"
import { useRef, useEffect } from "react"
import PropTypes from "prop-types"
import VisuallyHidden from "@reach/visually-hidden"
import Chart from "chart.js"

import useLocales from "hooks/use-locales"

const formatMoney = (
  amount,
  decimalCount = 2,
  decimal = ".",
  thousands = ","
) => {
  try {
    decimalCount = Math.abs(decimalCount)
    decimalCount = isNaN(decimalCount) ? 2 : decimalCount

    const negativeSign = amount < 0 ? "-" : ""

    let i = parseInt(
      (amount = Math.abs(Number(amount) || 0).toFixed(decimalCount))
    ).toString()
    let j = i.length > 3 ? i.length % 3 : 0

    return (
      negativeSign +
      (j ? i.substr(0, j) + thousands : "") +
      i.substr(j).replace(/(\d{3})(?=\d)/g, "$1" + thousands) +
      (decimalCount
        ? decimal +
          Math.abs(amount - i)
            .toFixed(decimalCount)
            .slice(2)
        : "")
    )
  } catch (e) {
    console.log(e)
  }
}

const useData = () => {
  const { t } = useLocales()

  return {
    datasets: [
      {
        data: [640774, 869808, 92680, 1428590, 372636, 56042, 191336],
        backgroundColor: [
          "#C2D5EC",
          "#6C9AD0",
          "#F18184",
          "#E8B28A",
          "#F8D977",
          "#B1CE94",
          "#716975",
        ],
      },
    ],
    labels: [
      t("chart.1"),
      t("chart.2"),
      t("chart.3"),
      t("chart.4"),
      t("chart.5"),
      t("chart.6"),
      t("chart.7"),
    ],
  }
}

const Circle = ({ bg }) => (
  <div
    sx={{
      height: "0.9rem",
      width: "0.9rem",
      borderRadius: "50%",
      mr: 2,
      bg,
    }}
  />
)

Circle.propTypes = {
  bg: PropTypes.string.isRequired,
}

export const Legend = () => {
  const { t } = useLocales()
  const data = useData()
  const dataset = data.datasets[0]
  const total = formatMoney(dataset.data.reduce((a, b) => a + b, 0))

  return (
    <table sx={{ borderSpacing: 8, p: 0, my: 3 }}>
      <VisuallyHidden>
        <thead>
          <tr>
            <th>{t("funding.source")}</th>
            <th>{t("funding.amount")}</th>
          </tr>
        </thead>
      </VisuallyHidden>
      <tbody>
        {dataset.data.map((value, index) => (
          <tr key={value}>
            <td>
              <div sx={{ display: "flex", alignItems: "center" }}>
                <Circle bg={dataset.backgroundColor[index]} />{" "}
                {data.labels[index]}
              </div>
            </td>
            <td sx={{ textAlign: "right" }}>{formatMoney(value)}</td>
          </tr>
        ))}
        <tr>
          <td>{t("total")}</td>
          <td sx={{ textAlign: "right" }}>{total}</td>
        </tr>
      </tbody>
    </table>
  )
}

const PieChart = () => {
  const container = useRef(null)
  const data = useData()

  useEffect(() => {
    if (container.current) {
      new Chart(container.current, {
        type: "pie",
        options: {
          //   aspectRatio: 1.75,
          legend: {
            display: false,
          },
          cutoutPercentage: 70,
          tooltips: {
            callbacks: {
              label: function (tooltipItem, data) {
                const label = data.labels[tooltipItem.index] || ""
                const value =
                  formatMoney(
                    data.datasets[tooltipItem.datasetIndex].data[
                      tooltipItem.index
                    ]
                  ) || ""

                return `${label}: ${value}`
              },
            },
          },
        },
        data,
      })
    }
  }, [container])

  return (
    <canvas
      id="funding-chart"
      ref={container}
      sx={{ width: "100%", height: "500px" }}
    />
  )
}

export default PieChart
